import React, { useMemo, useState } from 'react';
import { Table, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiAlertCircle } from 'react-icons/fi';
import WorkflowBadge from './WorkflowBadge';
import CheckStatusIndicators from './CheckStatusIndicators';
import { formatTiming, formatActivityWindow, formatShortDate } from '../utils/dateHelpers';
import { isTriggeredWorkflow } from '../utils/workflowLabels';
import OperationsEmptyState from './OperationsEmptyState';

const SORTABLE_COLUMNS = {
    policy_code: (a, b) => (a.policy_code || '').localeCompare(b.policy_code || ''),
    partner_name: (a, b) => (a.partner_name || '').localeCompare(b.partner_name || ''),
    date_to_check: (a, b) => (a.date_to_check || '').localeCompare(b.date_to_check || ''),
    workflow_label: (a, b) => (a.workflow_label || '').localeCompare(b.workflow_label || ''),
};

const getRowClassName = (row) => {
    if (row.workflow_label === 'observation_pending') {
        return 'ops-row-pending';
    }
    if (isTriggeredWorkflow(row.workflow_label)) {
        return 'ops-row-triggered';
    }
    return '';
};

const ObservationDataTable = ({
    items,
    loading,
    sortConfig,
    onSort,
    emptyMessage,
    emptySubMessage,
    compact,
}) => {
    const sortedItems = useMemo(() => {
        const sorter = SORTABLE_COLUMNS[sortConfig.key];
        if (!sorter) return items;

        return [...items].sort((a, b) => {
            const result = sorter(a, b);
            return sortConfig.direction === 'asc' ? result : -result;
        });
    }, [items, sortConfig]);

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" size="sm" role="status" />
                <span className="ms-2">Chargement…</span>
            </div>
        );
    }

    if (!sortedItems.length) {
        return (
            <OperationsEmptyState
                message={emptyMessage}
                subMessage={emptySubMessage}
                compact={compact}
            />
        );
    }

    return (
        <div className="table-responsive admin-ds-table-wrapper">
            <Table hover className="admin-ds-data-table mb-0">
                <thead>
                    <tr>
                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => onSort('policy_code')} role="button" tabIndex={0}>Code police</th>
                        <th scope="col">Réservation</th>
                        <th scope="col" style={{ cursor: 'pointer' }} className="operations-sortable-header" onClick={() => onSort('partner_name')} role="button" tabIndex={0}>Partenaire</th>
                        <th scope="col">Client</th>
                        <th scope="col" className="operations-sortable-header" onClick={() => onSort('date_to_check')} role="button" tabIndex={0}>Jour vérifié</th>
                        <th scope="col">Timing obs.</th>
                        <th scope="col">Type trigger</th>
                        <th scope="col">Distribution</th>
                        <th scope="col">Paiement</th>
                        <th scope="col" className="text-center">Modif.</th>
                        <th scope="col">Dates</th>
                        <th scope="col">Statut</th>
                        <th scope="col" className="operations-sortable-header" onClick={() => onSort('workflow_label')} role="button" tabIndex={0}>État</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedItems.map((row) => (
                        <tr key={`${row.policy_id}-${row.date_to_check}-obs`} className={getRowClassName(row)}>
                            <td>
                                {row.policy_id ? (
                                    <Link to={`/admin/trips/policies/${row.policy_id}`}>
                                        {row.policy_code || '—'}
                                    </Link>
                                ) : (
                                    row.policy_code || '—'
                                )}
                            </td>
                            <td>{row.reservation_number || '—'}</td>
                            <td>{row.partner_name || '—'}</td>
                            <td>{row.client_name || '—'}</td>
                            <td>{formatShortDate(row.date_to_check)}</td>
                            <td>{formatTiming(row.observation_timing_day, row.observation_timing_hour)}</td>
                            <td>{row.trigger_type || '—'}</td>
                            <td>
                                {row.distribution_type ? (
                                    <Badge bg="light" text="dark">{row.distribution_type}</Badge>
                                ) : '—'}
                            </td>
                            <td>{row.payment_schedule || '—'}</td>
                            <td className="text-center">
                                {row.trip_modified && (
                                    <FiAlertCircle
                                        className="text-warning"
                                        title="Dates de séjour modifiées"
                                        aria-label="Dates de séjour modifiées"
                                    />
                                )}
                            </td>
                            <td>{formatActivityWindow(row.activity_window)}</td>
                            <td>
                                <CheckStatusIndicators status={row.status} checkType="observation" />
                            </td>
                            <td>
                                <WorkflowBadge label={row.workflow_label} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

const ObservationTable = ({
    claims = null,
    forecastNotTriggered = null,
    loading = false,
    scope = 'all',
    emptyMessage,
    emptySubMessage,
    compact = false,
    claimsEmptyMessage = 'Aucun sinistre en attente d\'observation',
    fntEmptyMessage = 'Aucune prévision non déclenchée à re-vérifier',
}) => {
    const [sortConfig, setSortConfig] = useState({ key: 'date_to_check', direction: 'asc' });

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const claimsItems = claims || [];
    const fntItems = forecastNotTriggered || [];

    const showClaims = scope === 'all' || scope === 'claims';
    const showFnt = scope === 'all' || scope === 'forecast_not_triggered';

    if (!showClaims && !showFnt) {
        return null;
    }

    if (scope !== 'all') {
        const items = scope === 'claims' ? claimsItems : fntItems;
        const resolvedEmpty = emptyMessage || (scope === 'claims' ? claimsEmptyMessage : fntEmptyMessage);
        return (
            <ObservationDataTable
                items={items}
                loading={loading}
                sortConfig={sortConfig}
                onSort={handleSort}
                emptyMessage={resolvedEmpty}
                emptySubMessage={emptySubMessage}
                compact={compact}
            />
        );
    }

    return (
        <div className="ops-observation-sections">
            {showClaims && (
                <div className="mb-4">
                    <h6 className="text-secondary fw-semibold mb-3">Sinistres</h6>
                    <ObservationDataTable
                        items={claimsItems}
                        loading={loading}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        emptyMessage={claimsEmptyMessage}
                        emptySubMessage={emptySubMessage}
                        compact={compact}
                    />
                </div>
            )}
            {showFnt && (
                <div>
                    <h6 className="text-secondary fw-semibold mb-3">Prévisions non déclenchées</h6>
                    <ObservationDataTable
                        items={fntItems}
                        loading={loading}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        emptyMessage={fntEmptyMessage}
                        emptySubMessage={emptySubMessage}
                        compact={compact}
                    />
                </div>
            )}
        </div>
    );
};

export default ObservationTable;
